import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Drawer, { Field } from "@/components/Drawer";
import { StatusBadge, projectStatusMap } from "@/components/StatusBadge";
import { Plus, Trash2, Pencil, CheckCircle2, Circle, ChevronRight, Hammer } from "lucide-react";
import { fmtMoney, fmtKW, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  name: "", customer_id: "", deal_id: "",
  status: "planning", capacity_kw: "", contract_value: "", site_location: "",
  start_date: "", end_date: "", project_manager_id: "", progress: 0, notes: "",
};

export default function Projects() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [p, u, c, d] = await Promise.all([
      api.get("/projects"), api.get("/users"), api.get("/customers"), api.get("/deals"),
    ]);
    setRows(p.data); setUsers(u.data); setCustomers(c.data); setDeals(d.data);
    if (selected) {
      const fresh = p.data.find((x) => x.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    const payload = {
      ...data,
      capacity_kw: data.capacity_kw === "" ? null : Number(data.capacity_kw),
      contract_value: data.contract_value === "" ? null : Number(data.contract_value),
      progress: Number(data.progress) || 0,
      customer_id: data.customer_id || null,
      deal_id: data.deal_id || null,
      project_manager_id: data.project_manager_id || null,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
    };
    try {
      if (drawer.mode === "edit") await api.patch(`/projects/${drawer.data.id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Saved");
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete project?")) return;
    await api.delete(`/projects/${id}`);
    setSelected(null);
    load();
  };

  const cycleMilestone = async (p, m) => {
    const next = m.status === "pending" ? "in_progress" : m.status === "in_progress" ? "completed" : "pending";
    await api.patch(`/projects/${p.id}/milestone/${m.id}`, { status: next });
    load();
  };

  return (
    <div data-testid="projects-page">
      <PageHeader
        title="Projects"
        subtitle="Track solar EPC execution — from design to handover."
        actions={
          <button className="btn-primary flex items-center gap-2" data-testid="add-project-btn"
            onClick={() => setDrawer({ mode: "create", data: empty })}>
            <Plus className="w-4 h-4" /> New Project
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {rows.map((p) => {
            const cust = customers.find((c) => c.id === p.customer_id);
            const pm = users.find((u) => u.id === p.project_manager_id);
            return (
              <div key={p.id} className={`crm-card p-4 cursor-pointer ${selected?.id === p.id ? "border-[#10B981] ring-2 ring-[#10B981]/20" : ""}`}
                onClick={() => setSelected(p)} data-testid={`project-row-${p.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{cust?.name || "—"} · {p.site_location || "—"}</div>
                  </div>
                  <StatusBadge value={p.status} map={projectStatusMap} />
                </div>
                <div className="grid grid-cols-4 gap-4 mt-3 text-xs">
                  <Stat label="Capacity" v={p.capacity_kw ? fmtKW(p.capacity_kw) : "—"} />
                  <Stat label="Value" v={p.contract_value ? fmtMoney(p.contract_value) : "—"} />
                  <Stat label="PM" v={pm?.name || "—"} />
                  <Stat label="Start" v={fmtDate(p.start_date)} />
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500">Progress</span>
                    <span className="font-mono text-slate-700">{p.progress || 0}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#10B981] to-[#0F3A66] rounded-full transition-all"
                      style={{ width: `${p.progress || 0}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="crm-card p-12 text-center text-slate-500">
              <Hammer className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No projects yet.
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-1">
          {selected ? (
            <div className="crm-card p-5 sticky top-6" data-testid="project-detail">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">{selected.name}</h3>
                  <p className="text-xs text-slate-500">Milestones</p>
                </div>
                <div className="flex gap-1">
                  <button className="btn-ghost !p-1.5" onClick={() => setDrawer({ mode: "edit", data: { ...empty, ...selected } })}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button className="btn-ghost !p-1.5 text-red-600" onClick={() => remove(selected.id)}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {(selected.milestones || []).map((m) => (
                  <button
                    key={m.id}
                    onClick={() => cycleMilestone(selected, m)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-[#10B981] transition-colors text-left"
                    data-testid={`milestone-${m.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {m.status === "completed" ? (
                        <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                      ) : m.status === "in_progress" ? (
                        <div className="w-4 h-4 rounded-full border-2 border-[#F59E0B] border-t-transparent animate-spin" />
                      ) : (
                        <Circle className="w-4 h-4 text-slate-400" />
                      )}
                      <span className={`text-sm ${m.status === "completed" ? "text-slate-500 line-through" : "text-slate-800"}`}>
                        {m.name}
                      </span>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                ))}
              </div>
              {selected.notes && (
                <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600">
                  {selected.notes}
                </div>
              )}
            </div>
          ) : (
            <div className="crm-card p-8 text-center text-slate-500 text-sm">
              Select a project to view milestones and details.
            </div>
          )}
        </div>
      </div>

      {drawer && (
        <ProjectDrawer drawer={drawer} onClose={() => setDrawer(null)} onSave={save}
          users={users} customers={customers} deals={deals} />
      )}
    </div>
  );
}

function ProjectDrawer({ drawer, onClose, onSave, users, customers, deals }) {
  const [data, setData] = useState(drawer.data);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  return (
    <Drawer open onClose={onClose} title={drawer.mode === "edit" ? "Edit Project" : "New Project"}
      wide testid="project-drawer"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" data-testid="project-save-btn" onClick={() => onSave(data)}>Save</button>
        </>
      }
    >
      <Field label="Project Name *"><input className="input-base" value={data.name} data-testid="project-name" onChange={(e) => set("name", e.target.value)} /></Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Field label="Customer">
          <select className="input-base" value={data.customer_id || ""} onChange={(e) => set("customer_id", e.target.value)}>
            <option value="">—</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="From Deal">
          <select className="input-base" value={data.deal_id || ""} onChange={(e) => set("deal_id", e.target.value)}>
            <option value="">—</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select className="input-base" value={data.status} onChange={(e) => set("status", e.target.value)}>
            {Object.entries(projectStatusMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        <Field label="Project Manager">
          <select className="input-base" value={data.project_manager_id || ""} onChange={(e) => set("project_manager_id", e.target.value)}>
            <option value="">—</option>
            {users.filter((u) => u.role === "project_manager" || u.role === "admin").map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Capacity (kW)"><input className="input-base" type="number" value={data.capacity_kw ?? ""} onChange={(e) => set("capacity_kw", e.target.value)} /></Field>
        <Field label="Contract Value (₹)"><input className="input-base" type="number" value={data.contract_value ?? ""} onChange={(e) => set("contract_value", e.target.value)} /></Field>
        <Field label="Site Location"><input className="input-base" value={data.site_location || ""} onChange={(e) => set("site_location", e.target.value)} /></Field>
        <Field label="Progress %"><input className="input-base" type="number" min="0" max="100" value={data.progress || 0} onChange={(e) => set("progress", e.target.value)} /></Field>
        <Field label="Start Date"><input className="input-base" type="date" value={(data.start_date || "").slice(0, 10)} onChange={(e) => set("start_date", e.target.value)} /></Field>
        <Field label="End Date"><input className="input-base" type="date" value={(data.end_date || "").slice(0, 10)} onChange={(e) => set("end_date", e.target.value)} /></Field>
      </div>
      <Field label="Notes"><textarea className="input-base min-h-[80px]" value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
    </Drawer>
  );
}

function Stat({ label, v }) {
  return (
    <div>
      <div className="text-slate-500 uppercase tracking-widest text-[10px]">{label}</div>
      <div className="text-slate-800 font-medium mt-0.5">{v}</div>
    </div>
  );
}
